import React, { useState, useEffect, ReactElement } from 'react';
import {
  Modal,
  Icon,
  Header,
  Dimmer,
  Loader,
  Grid,
  Accordion,
  Popup,
  Message,
} from 'semantic-ui-react';
import { getFilesLinkAxios, syncDecisionDocuments } from '../../services/file';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../services/notification';
import { FieldLabel } from '../../styles';
import FileUploader, { FileAttachmentProps } from '../UploadFiles';
import { fileTypeProps } from '../UploadFilesUncontroled';
import JSZip from 'jszip';
import axios from 'axios';
import { saveAs } from 'file-saver';
import AuthorizationContainer from '../AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { FollowUpContextEnum } from '../../enums/followup-context.enum';
import Button from '../Button';

export type ModalAttachFilesProps = {
  bondInsuranceId: string;
  message?: string;
  tableName?: string;
  trigger?: ReactElement;
  fileIdentifiers?: fileTypeProps[];
  callback?: (b: string[], files: any[]) => void;
  brokerOnly?: boolean;
  tenantName?: string;
  disabled?: boolean;
  hideModels?: boolean;
  tags?: {
    tag: string;
    name: string;
    date?: Date;
  }[];
  enableDecisionDocumentFeatures?: boolean;
  /**
   * Executado antes da modal abrir e da primeira busca de arquivos.
   * Util para garantir (de forma idempotente) que um documento gerado
   * automaticamente ja exista antes de listar os anexos.
   */
  onBeforeOpen?: () => Promise<void>;
};

const ModalAttachFiles = ({
  bondInsuranceId,
  tableName = 'bond_insurance',
  trigger,
  fileIdentifiers,
  callback,
  brokerOnly = true,
  tenantName,
  disabled = false,
  hideModels = false,
  tags = [],
  enableDecisionDocumentFeatures = false,
  onBeforeOpen,
}: ModalAttachFilesProps) => {
  const [loading, setLoading] = useState(true);
  const [loadingZip, setLoadingZip] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [attachFilesModal, setAttachFilesModal] = useState(false);
  const [flagSubmitAttachments, setFlagSubmitAttachments] = useState(false);
  const [attachedLinkFiles, setAttachedLinkFiles] = useState<FileAttachmentProps[]>([]);

  const [openModels, setOpenModels] = useState(false);

  const handleOpenModel = () => {
    setOpenModels(!openModels);
  };

  // Semantic UI Dropdown registra keydown em capture no document (EventStack).
  // Com focus=true, Space chama preventDefault/openOnSpace mesmo com a modal aberta.
  useEffect(() => {
    if (!attachFilesModal) return;

    const isModalEditable = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement => {
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) {
        return false;
      }
      return !!target.closest('.ui.modal');
    };

    const blurBackgroundDropdowns = () => {
      document.querySelectorAll('.ui.dropdown').forEach(node => {
        if (node.closest('.ui.modal')) return;
        (node as HTMLElement).blur();
        node.classList.remove('active', 'visible');
        node.querySelectorAll('.menu.visible').forEach(menu => {
          menu.classList.remove('visible', 'animating', 'transition');
        });
      });

      const active = document.activeElement as HTMLElement | null;
      if (active && !active.closest('.ui.modal')) {
        active.blur();
      }
    };

    const insertCharacter = (
      el: HTMLInputElement | HTMLTextAreaElement,
      char: string
    ) => {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = `${el.value.slice(0, start)}${char}${el.value.slice(end)}`;
      if (el.maxLength > 0 && next.length > el.maxLength) return;

      const proto =
        el instanceof HTMLTextAreaElement
          ? HTMLTextAreaElement.prototype
          : HTMLInputElement.prototype;
      Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, next);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      const pos = start + char.length;
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        // ignore unsupported input types
      }
    };

    // Capture no document: o EventStack do Dropdown também usa capture e pode
    // rodar antes ou depois. Sempre assumimos o Space nos inputs da modal.
    const guardSpaceInModal = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (!isModalEditable(e.target)) return;

      blurBackgroundDropdowns();
      e.preventDefault();
      e.stopImmediatePropagation();
      insertCharacter(e.target, ' ');
    };

    blurBackgroundDropdowns();
    const t0 = window.setTimeout(blurBackgroundDropdowns, 0);
    const t1 = window.setTimeout(blurBackgroundDropdowns, 50);

    // window capture roda antes do EventStack (document capture)
    window.addEventListener('keydown', guardSpaceInModal, true);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.removeEventListener('keydown', guardSpaceInModal, true);
    };
  }, [attachFilesModal]);

  async function refreshFiles() {
    try {
      setLoading(true);
      const response = await getFilesLinkAxios(bondInsuranceId, tableName);
      if (response.urls?.length > 0) {
        const filesAttached = response.urls.map((url: any) => ({
          fileLink: url.link,
          option: url.name,
          id: url.id,
          createdAt: url.createdAt,
          label: url.label || null,
          decisionDocument: url.decisionDocument || null,
          tag:
            tags?.length > 1 && tags[1]?.date !== undefined
              ? url.createdAt < tags[1].date
                ? tags[0]
                : tags[1]
              : ''
        }));

        setAttachedLinkFiles(
          filesAttached.sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
      } else {
        setAttachedLinkFiles([]);
      }
    } catch (err) {
      console.log(err);
      dangerNotification('Ops...', 'Erro ao tentar recuperar o link dos arquivos!');
    } finally {
      setLoading(false);
    }
  }

  async function handleSyncDecisionDocuments() {
    try {
      setLoadingSync(true);
      const result = await syncDecisionDocuments(bondInsuranceId, {
        forceRefresh: true,
      });
      await refreshFiles();
      const synced =
        typeof result?.synced === 'number' ? result.synced : null;
      if (synced === 0) {
        warningNotification(
          'Nenhuma carta nova',
          'Não encontramos documento novo na seguradora neste momento. Os anexos abaixo continuam válidos; tente novamente depois ou envie a carta manualmente.'
        );
      } else if (synced != null && synced > 0) {
        successNotification(
          'Cartas atualizadas',
          synced === 1
            ? '1 documento foi sincronizado ou registrado.'
            : `${synced} documentos foram sincronizados ou registrados.`
        );
      } else {
        successNotification(
          'Sincronização concluída',
          'A lista de anexos foi atualizada.'
        );
      }
    } catch (error) {
      warningNotification(
        'Não foi possível atualizar',
        'A seguradora pode estar indisponível ou este tipo de carta ainda não existe para esta análise. Você pode tentar de novo mais tarde ou anexar o arquivo manualmente.'
      );
    } finally {
      setLoadingSync(false);
    }
  }

  async function callbackFilesAttachment(files: any) {
    setLoading(true);
    setFlagSubmitAttachments(false);

    // Se houve erro no upload (reportado pelo filho), não fecha a modal
    if (files?.error) {
      setLoading(false);
      return;
    }

    try {
      if (attachFilesModal && bondInsuranceId) {
        const response = await getFilesLinkAxios(bondInsuranceId, tableName);
        if (response.urls?.length > 0) {
          const filesAttached = response.urls.map((url: any) => ({
            fileLink: url.link,
            option: url.name,
            id: url.id,
            createdAt: url.createdAt,
            label: url.label || null,
            decisionDocument: url.decisionDocument || null,
          }));

          setAttachedLinkFiles(
            filesAttached.sort(function (a: any, b: any) {
              return b.createdAt - a.createdAt;
            })
          );

          if (callback && files && Array.isArray(files) && files[0]?.contextType) {
            const callbackReturn = files.map((c: any) => c.contextType);
            const matchedFiles = filesAttached.filter((fa: any) => files.some((f: any) => f.id === fa.id));
            callback(callbackReturn, matchedFiles);
          }
        }
      }
      setAttachFilesModal(false);
    } catch (err) {
      console.log(err);
      dangerNotification('Ops...', 'Erro ao tentar recuperar o link dos arquivos!');
      setAttachFilesModal(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (attachFilesModal) {
      refreshFiles();
    } else {
      setAttachedLinkFiles([]);
    }
  }, [attachFilesModal, bondInsuranceId, tableName]);

  useEffect(() => {
    fileIdentifiers?.sort(function compare(a, b) {
      return a.name.localeCompare(b.name, 'pt-BR');
    });
  }, [fileIdentifiers]);

  async function downloadFiles() {
    try {
      setLoadingZip(true);
      const zip = new JSZip();

      const filePromises = attachedLinkFiles.map(file =>
        axios.get(file.fileLink, { responseType: 'arraybuffer' }).then(response => {
          const originalFileName =
            file.fileLink.split('?')[0].split('/').pop()?.slice(37) || '';
          const typeName =
            fileIdentifiers?.find(f => f.key === file.option)?.name || file.option;
          const identifier = (file.label || typeName || 'arquivo')
            .replace(/[^\wÀ-ÿ\s-]/gi, '')
            .trim()
            .replace(/\s+/g, '_');
          const fileName = `${identifier}_${originalFileName}`;
          zip.file(fileName!, response.data, { binary: true });
        })
      );

      await Promise.all(filePromises);

      zip.generateAsync({ type: 'blob' }).then(content => {
        saveAs(content, tenantName ? `anexos_${tenantName}.zip` : 'anexos.zip');
      });

      successNotification('Sucesso', 'Download realizado com sucesso!');
    } catch (e) {
      console.log(e);
      dangerNotification(
        'Oops...',
        'Ocorreu um erro ao realizar o download dos arquivos!'
      );
    } finally {
      setLoadingZip(false);
    }
  }

  return (
    <Modal
      closeIcon
      open={attachFilesModal}
      onClose={() => setAttachFilesModal(false)}
      trigger={
        trigger ? (
          trigger
        ) : (
          <Button type="button" style={{ backgroundColor: '#59249B' }}>
            <Icon name={'attach'} />
            Gerenciar Anexos
          </Button>
        )
      }
      onOpen={async () => {
        if (onBeforeOpen) {
          try {
            await onBeforeOpen();
          } catch (err) {
            console.log(err);
          }
        }
        setAttachFilesModal(true);
      }}
    >
      <Header icon="file alternate outline">
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            {' '}
            <Icon name="file alternate outline" /> Gerenciar Anexos
          </div>
          <Popup
            content={'Clique para realizar o download de todos os anexos!'}
            position={'top right'}
            trigger={
              <AuthorizationContainer
                action={ActionEnum.download}
                feature={FeatureEnum.files}
              >
                <Button
                  icon="download"
                  onClick={downloadFiles}
                  loading={loadingZip}
                  disabled={attachedLinkFiles.length === 0 || loadingZip}
                  style={{ backgroundColor: '#59249B', color: 'white' }}
                />
              </AuthorizationContainer>
            }
          />
          {enableDecisionDocumentFeatures && (
            <AuthorizationContainer
              action={ActionEnum.create}
              feature={FeatureEnum.files}
            >
              <Popup
                content={
                  'Busca na seguradora a carta de aprovação ou recusa mais recente (quando a integração estiver disponível). Não substitui arquivos que você enviou manualmente.'
                }
                position="top right"
                wide
                trigger={
                  <div style={{ marginLeft: 8, display: 'inline-block' }}>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      onClick={handleSyncDecisionDocuments}
                      loading={loadingSync}
                      disabled={loadingSync || loading}
                      style={{ backgroundColor: '#59249B', color: 'white' }}
                    >
                      <Icon name="refresh" />
                      Atualizar cartas
                    </Button>
                  </div>
                }
              />
            </AuthorizationContainer>
          )}
        </div>
      </Header>

      <Modal.Content>
        <Dimmer active={loading}>
          <Loader indeterminate> Carregando... </Loader>
        </Dimmer>
        <div>
          <p>
            São aceitos arquivos de até <b>50MB</b> <br />
            Formatos Aceitos: jpeg, png, webp, doc, docx, pdf e odt.
          </p>

          {!hideModels &&
            tableName !== 'capitalization' &&
            tableName !== FollowUpContextEnum.analysis && (
              <Accordion>
                <Accordion.Title active={openModels} onClick={handleOpenModel}>
                  <Icon name="dropdown" color="blue" size="big" />
                  <b>Modelos de arquivos para solicitações de cancelamentos.</b>
                </Accordion.Title>
                <Accordion.Content active={openModels}>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <FieldLabel>
                          - Termo cancelamento compra do imóvel. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+compra+do+im%C3%B3vel.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento de desistência de locação. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+de+desist%C3%AAncia+de+loca%C3%A7%C3%A3o.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento de entrega de chaves e inexistência de
                          débitos. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+de+entrega+de+chaves+e+inexist%C3%AAncia+de+d%C3%A9bitos.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento de troca de locatário. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+de+troca+de+locat%C3%A1rio.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento de troca de seguradora. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+de+troca+de+seguradora.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento duplicidade de apólice. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+duplicidade+de+ap%C3%B3lice.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo cancelamento troca de garantia. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+cancelamento+troca+de+garantia.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Termo de inexistência de débitos. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Termo+de+inexistencia+de+debitos.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                        <FieldLabel>
                          - Distrato locador imobiliária e inexistência de débitos. (
                          <a href="https://4seg-documents-analysis.s3.amazonaws.com/Modelos+Cancelamento/Distrato+locador+imobiliaria+e+inexistencia+de+debitos.docx">
                            Baixar Modelo
                          </a>
                          )<br />
                        </FieldLabel>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Accordion.Content>
              </Accordion>
            )}

          <FileUploader
            callback={(data: any) => callbackFilesAttachment(data)}
            registerId={bondInsuranceId}
            tableName={tableName}
            controlSubmitFiles={flagSubmitAttachments}
            filesAttachs={attachedLinkFiles}
            fileIdentifiers={fileIdentifiers}
            brokerOnly={brokerOnly}
            disabled={disabled}
            enableDecisionDocumentFeatures={enableDecisionDocumentFeatures}
          />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button color="red" onClick={() => setAttachFilesModal(false)}>
          <Icon name="remove" /> Fechar {disabled ? 'anexos' : 'sem fazer upload'}
        </Button>
        {!disabled && (
          <AuthorizationContainer action={ActionEnum.upload} feature={FeatureEnum.files}>
            <Button color="green" onClick={() => setFlagSubmitAttachments(true)}>
              <Icon name="checkmark" />
              Fazer upload dos arquivos anexados
            </Button>
          </AuthorizationContainer>
        )}
      </Modal.Actions>
    </Modal>
  );
};
export default ModalAttachFiles;
